// Company intelligence beside triage (module 10 U54, U59; the drawing's frames 1, 2a and 4; B517).
//
// Two views of the same newest read-out, and the stylesheet decides which one shows (intel.css):
//   CompanyBar     on a wide window (1,304px and over), a bar to the right of the ten, in the 320px the
//                  ten's column (U40) leaves beside it, so the ten keeps its width;
//   CompanyLines   below that (beside Outlook, a phone), three lines under the score bug, with a link to
//                  the page. Mike answered "I prefer 1-triage" (the bar); where the lines sit beside
//                  Outlook is his to confirm on the built page (DESIGN, Company intelligence, Frontier 1).
// Both share one request a minute (api.ts). Nothing is drawn until there is a read-out: no placeholder,
// no empty panel beside the ten. A line opens the page; the page opens its sources (U58).
import { Chip, CompanyName } from '../parts/index.js';
import { useEffect, useState, type MouseEvent } from 'react';
import { companyIntel, type CompanyIntel } from './api.js';
import { moreWords, morningHour, newTag, sourceChips, whenOf, writtenWords } from './words.js';
import './intel.css';

const BAR_COMPANIES = 6;
const BAR_LINES = 2;

function useNewest(): CompanyIntel | null {
  const [view, setView] = useState<CompanyIntel | null>(null);
  useEffect(() => {
    let cancelled = false;
    const read = () => companyIntel().then((v) => { if (!cancelled) setView(v); }, () => { /* the page says why; triage stays as it is */ });
    read();
    // The hour's addition: look again every ten minutes while triage is open.
    const t = window.setInterval(read, 10 * 60_000);
    return () => { cancelled = true; window.clearInterval(t); };
  }, []);
  return view;
}

const hasLines = (v: CompanyIntel | null): v is CompanyIntel => !!v && v.day !== null && (v.companies.length > 0 || v.handOns.length > 0);

export function CompanyBar({ go }: { go: (path: string) => void }) {
  const v = useNewest();
  if (!hasLines(v)) return null;
  const open = (e: MouseEvent) => { e.preventDefault(); go('/intelligence/company'); };
  const shown = v.companies.slice(0, BAR_COMPANIES);
  const more = moreWords(v.companies.length - shown.length, 0);
  return (
    <aside className="cib" aria-label="Company intelligence">
      <div className="cib-inner">
      <div className="cib-h">
        <h2>Company intelligence</h2>
        <a href="/intelligence/company" onClick={open}>Open the page</a>
      </div>
      <p className="cib-when">
        {writtenWords(v).slice(0, 1).map((w) => <span key={w}>{w}</span>)}
        {v.newSinceMorning > 0 && morningHour(v) ? <span>{v.newSinceMorning} new since {morningHour(v)}</span> : null}
      </p>
      {v.handOns.length > 0 ? (
        <div className="cib-sec">
          <h3>What came of your hand-ons</h3>
          {v.handOns.slice(0, 3).map((h) => (
            <p key={h.id} className="cib-ln">
              {newTag(h) ? <Chip className="ci-new">{newTag(h)}</Chip> : null}{h.text}
              <span className="cib-src">{sourceChips(h.sources).map((c) => <span key={c} className="chip">{c}</span>)}</span>
            </p>
          ))}
        </div>
      ) : null}
      <div className="cib-sec">
        {shown.map((c) => (
          <div key={c.organisationId} className="cib-co">
            <div className="cib-co-h"><b><CompanyName id={c.organisationId} name={c.name} go={go} /></b><span>{c.label}</span></div>
            {c.lines.slice(0, BAR_LINES).map((l) => (
              <p key={l.id} className="cib-ln">
                {newTag(l) ? <Chip className="ci-new">{newTag(l)}</Chip> : null}{l.text}
                <span className="cib-src">
                  {l.people.length > 0 ? <span>{l.people.join(', ')}</span> : null}
                  {sourceChips(l.sources).map((x) => <span key={x} className="chip">{x}</span>)}
                  {whenOf(l.happenedAt) ? <span>{whenOf(l.happenedAt)}</span> : null}
                </span>
              </p>
            ))}
          </div>
        ))}
      </div>
      {more ? <p className="cib-more"><a href="/intelligence/company" onClick={open}>{more}</a></p> : null}
      </div>
    </aside>
  );
}

export function CompanyLines({ go }: { go: (path: string) => void }) {
  const v = useNewest();
  if (!hasLines(v)) return null;
  const open = (e: MouseEvent) => { e.preventDefault(); go('/intelligence/company'); };
  const top = v.companies.slice(0, 3);
  const more = moreWords(v.companies.length - top.length, v.handOns.length);
  return (
    <div className="ci3" aria-label="Company intelligence">
      <div className="ci3-h">
        <h2><a href="/intelligence/company" onClick={open}>Company intelligence</a></h2>
        <span>{v.newSinceMorning > 0 ? `${v.newSinceMorning} new since ${writtenWords(v)[0]?.replace('Written ', '') ?? 'this morning'}` : writtenWords(v)[0] ?? ''}</span>
      </div>
      {top.map((c) => {
        const l = c.lines[0]!;
        return (
          <p key={c.organisationId}>
            <b><CompanyName id={c.organisationId} name={c.name} go={go} /></b>
            <span>{l.text}{l.people.length > 0 ? ` (${l.people[0]!.split(' ')[0]})` : ''}</span>
          </p>
        );
      })}
      {top.length === 0 && v.handOns.length > 0 ? <p><b>Your hand-ons</b><span>{v.handOns[0]!.text}</span></p> : null}
      {more ? <p className="all"><a href="/intelligence/company" onClick={open}>{more}</a></p> : null}
    </div>
  );
}
